/**
 * RolesPermissions — Sorumluluklar & Kullanıcı Atamaları (Faz 51)
 *
 * Yeni model: Sorumluluk (pozisyon/görev) şablonu + Kullanıcı × Sorumluluk ataması.
 * - Sekme 1: "Sorumluluklar" → şablonları yönet (ekle/düzenle/sil), her birine modül erişim seti ata
 * - Sekme 2: "Kullanıcı Atamaları" → her kullanıcıya bir veya birden fazla sorumluluk ata
 * - Admin (yonetici) sorumluluktan bağımsız tüm modüllere erişir (bypass)
 *
 * Yalnızca admin kullanıcılar bu sayfayı görür (App.js AdminOnly ile korunur).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  ShieldStar, MagnifyingGlass, Crown, Info, Plus, PencilSimple, TrashSimple,
  UsersThree, GridFour, Lock, Buildings, CaretDown, Shield,
} from "@phosphor-icons/react";

const EMPTY_RESP = { name: "", description: "", module_access: [] };
const ROOT_ADMIN_EMAIL = "onurselcuk@mssinsaat.com.tr";

// --------------------------------------------------------------------------
// UserSitesPopover — Faz 53
// Kullanıcının erişebildiği şantiyeleri tek popover ile seçer. "Tümü" veya
// belirli şantiye ID'leri. Admin ise disabled (tüm şantiyelere otomatik).
// --------------------------------------------------------------------------
function UserSitesPopover({ user: u, sites, disabled, onChange }) {
  const isAdminLocal = (u.role || "").toLowerCase() === "yonetici";
  const selected = Array.isArray(u.sites) ? u.sites : [];
  // Admin → tümü (gerçek site_id listesi gerekmez)
  const label = isAdminLocal
    ? "Tüm Şantiyeler (admin)"
    : selected.length === 0
      ? "Şantiye yok"
      : selected.length === sites.length && sites.length > 0
        ? "Tüm Şantiyeler"
        : `${selected.length} şantiye`;
  const tone = isAdminLocal
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : selected.length === 0
      ? "bg-slate-100 text-slate-500 border-slate-200"
      : "bg-sky-50 text-sky-800 border-sky-200";
  const allSelected = selected.length === sites.length && sites.length > 0;

  const toggleSite = (sid) => {
    if (selected.includes(sid)) {
      onChange(selected.filter((x) => x !== sid));
    } else {
      onChange([...selected, sid]);
    }
  };
  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange(sites.map((s) => s.id));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${tone} ${disabled ? "opacity-70 cursor-not-allowed" : "hover:brightness-95"}`}
          data-testid={`user-sites-btn-${u.email}`}
        >
          <Buildings size={12} />
          {label}
          {!isAdminLocal && <CaretDown size={10} />}
        </button>
      </PopoverTrigger>
      {!isAdminLocal && (
        <PopoverContent className="w-80 p-3" align="end">
          <div className="text-xs font-semibold text-slate-700 mb-2">
            Erişebildiği Şantiyeler
          </div>
          <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-slate-50 rounded">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            <span className="text-xs font-medium">Tüm Şantiyeler</span>
          </label>
          <div className="border-t border-slate-100 my-1.5" />
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {sites.map((s) => (
              <label key={s.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-slate-50 rounded">
                <Checkbox
                  checked={selected.includes(s.id)}
                  onCheckedChange={() => toggleSite(s.id)}
                />
                <span className="text-xs">
                  <span className="font-mono text-slate-500 mr-1">{s.code}</span>
                  {s.name}
                </span>
              </label>
            ))}
            {sites.length === 0 && (
              <div className="text-xs text-slate-400 italic p-2">
                Şantiye bulunamadı
              </div>
            )}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

export default function RolesPermissions() {
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const [tab, setTab] = useState("responsibilities");
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [modules, setModules] = useState([]);
  const [responsibilities, setResponsibilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUid, setSavingUid] = useState(null);
  const [search, setSearch] = useState("");
  // Sorumluluk edit dialog
  const [respOpen, setRespOpen] = useState(false);
  const [respEditing, setRespEditing] = useState(null);
  const [respForm, setRespForm] = useState(EMPTY_RESP);
  const [respSaving, setRespSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, m, r, s] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/modules/catalog`),
        axios.get(`${API}/responsibilities`),
        axios.get(`${API}/sites`),
      ]);
      setUsers(u.data || []);
      setModules(m.data?.modules || []);
      setResponsibilities(r.data?.items || []);
      setSites(Array.isArray(s.data) ? s.data : (s.data?.items || []));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q),
    );
  }, [users, search]);

  const isAdmin = (u) => (u.role || "").toLowerCase() === "yonetici";

  // ---------- Sorumluluk CRUD ----------
  const openCreateResp = () => {
    setRespEditing(null);
    setRespForm(EMPTY_RESP);
    setRespOpen(true);
  };
  const openEditResp = (r) => {
    setRespEditing(r);
    setRespForm({
      name: r.name,
      description: r.description || "",
      module_access: r.module_access || [],
    });
    setRespOpen(true);
  };
  const toggleRespModule = (slug) => {
    setRespForm((f) => {
      const has = f.module_access.includes(slug);
      return {
        ...f,
        module_access: has
          ? f.module_access.filter((s) => s !== slug)
          : [...f.module_access, slug],
      };
    });
  };
  const saveResp = async () => {
    if (!respForm.name.trim()) {
      toast.error("Sorumluluk ismi zorunludur");
      return;
    }
    setRespSaving(true);
    try {
      const payload = {
        name: respForm.name.trim(),
        description: respForm.description.trim(),
        module_access: respForm.module_access,
      };
      if (respEditing) {
        await axios.put(`${API}/responsibilities/${respEditing.id}`, payload);
        toast.success("Sorumluluk güncellendi");
      } else {
        await axios.post(`${API}/responsibilities`, payload);
        toast.success("Sorumluluk oluşturuldu");
      }
      setRespOpen(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRespSaving(false); }
  };
  const deleteResp = async (r) => {
    const ok = await askConfirm({
      title: "Sorumluluğu sil",
      description: `"${r.name}" sorumluluğunu silmek istiyor musunuz? Kullanıcı atamaları varsa işlem engellenir.`,
      confirmLabel: "Sil",
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/responsibilities/${r.id}`);
      toast.success("Sorumluluk silindi");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---------- Kullanıcı × Sorumluluk (Faz 54 — tek sorumluluk) ----------
  // user.responsibilities: [id] (en fazla 1) + user.sites: [site_id,...]
  const currentRespId = (u) => {
    const list = Array.isArray(u.responsibilities) ? u.responsibilities : [];
    return list[0] || "";
  };

  const saveUser = async (u, nextRespId, nextSites) => {
    const prevResps = u.responsibilities || [];
    const prevSites = u.sites || [];
    const nextResps = nextRespId ? [nextRespId] : [];
    setUsers((old) => old.map((x) =>
      x.id === u.id ? { ...x, responsibilities: nextResps, sites: nextSites } : x,
    ));
    setSavingUid(u.id);
    try {
      await axios.put(`${API}/users/${u.id}/responsibilities`, {
        responsibilities: nextResps,
        sites: nextSites,
      });
      const refreshed = (await axios.get(`${API}/users`)).data || [];
      setUsers(refreshed);
    } catch (e) {
      setUsers((old) => old.map((x) =>
        x.id === u.id ? { ...x, responsibilities: prevResps, sites: prevSites } : x,
      ));
      toast.error(formatErr(e));
    } finally { setSavingUid(null); }
  };

  const setUserResp = async (u, respId) => {
    // "none" / "" → sorumluluk kaldır
    await saveUser(u, respId === "__none__" ? "" : respId, u.sites || []);
  };

  const setUserSites = async (u, nextSites) => {
    await saveUser(u, currentRespId(u), nextSites);
  };

  // ---------- Admin Toggle ----------
  const toggleAdmin = async (u, nextIsAdmin) => {
    if ((u.email || "").toLowerCase() === ROOT_ADMIN_EMAIL && !nextIsAdmin) {
      toast.error("Root yönetici yöneticilikten çıkarılamaz");
      return;
    }
    const prev = u.role;
    const nextRole = nextIsAdmin ? "yonetici" : "kullanici";
    setUsers((old) => old.map((x) =>
      x.id === u.id ? { ...x, role: nextRole, role_key: nextRole } : x,
    ));
    setSavingUid(u.id);
    try {
      await axios.put(`${API}/users/${u.id}/admin`, { is_admin: nextIsAdmin });
      toast.success(nextIsAdmin ? "Yönetici olarak atandı" : "Yöneticilik kaldırıldı");
    } catch (e) {
      setUsers((old) => old.map((x) =>
        x.id === u.id ? { ...x, role: prev } : x,
      ));
      toast.error(formatErr(e));
    } finally { setSavingUid(null); }
  };

  const effectiveSummary = (u) => {
    const admin = isAdmin(u);
    if (admin) return "Tüm modüller (admin bypass)";
    const eff = Array.isArray(u.effective_module_access) ? u.effective_module_access : [];
    if (eff.length === 0) return "Erişim yok";
    if (eff.length === modules.length) return "Tüm modüller";
    return `${eff.length} / ${modules.length} modül`;
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6" data-testid="roles-permissions-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldStar size={26} weight="duotone" className="text-[#1B4776]" />
            Sorumluluklar & Yetkilendirme
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pozisyon/görev şablonları oluşturun, modül erişimlerini atayın ve
            kullanıcılara sorumluluk atayın. Admin kullanıcılar sorumluluktan
            bağımsız tüm modüllere erişir.
          </p>
        </div>

        {/* Info */}
        <Card className="p-4 border-amber-200 bg-amber-50/60">
          <div className="flex items-start gap-3">
            <Info size={20} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <b>Çalışma Prensibi:</b> Her kullanıcıya <b>tek bir sorumluluk</b>
              atanır — modül erişimi bu sorumluluğun modül kümesinden gelir.
              Şantiye kapsamı "Erişebildiği Şantiyeler" sütunundan ayrı seçilir.
              Admin (yönetici) rolü whitelist'i yok sayar. Finansal Kontrol ve
              Gayri Resmi Kasa gibi gizli modüller bu matrisin kapsamında
              <b> değildir</b> — ayrı root-email whitelist'i ile yönetilir.
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="responsibilities" data-testid="tab-responsibilities">
              <GridFour size={16} className="mr-1.5" />
              Sorumluluklar
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-user-assignments">
              <UsersThree size={16} className="mr-1.5" />
              Kullanıcı Atamaları
            </TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins">
              <Shield size={16} className="mr-1.5" />
              Yöneticiler
            </TabsTrigger>
          </TabsList>

          {/* ---------- Sorumluluklar sekmesi ---------- */}
          <TabsContent value="responsibilities" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Toplam <b>{responsibilities.length}</b> sorumluluk şablonu
              </div>
              <Button
                onClick={openCreateResp}
                className="bg-[#1B4776] hover:bg-[#163a63] text-white"
                data-testid="btn-add-responsibility"
              >
                <Plus size={16} className="mr-1.5" />
                Yeni Sorumluluk
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full text-center py-12 text-slate-400">Yükleniyor…</div>
              ) : responsibilities.length === 0 ? (
                <div className="col-span-full text-center py-12 text-slate-400">
                  Henüz sorumluluk şablonu yok. Yukarıdaki butondan ekleyin.
                </div>
              ) : responsibilities.map((r) => (
                <Card key={r.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`resp-card-${r.key}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-900">{r.name}</h3>
                      {r.is_system && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">
                          <Lock size={10} weight="fill" /> Sistem şablonu
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="icon"
                        className="h-7 w-7"
                        onClick={() => openEditResp(r)}
                        data-testid={`btn-edit-resp-${r.key}`}
                      >
                        <PencilSimple size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => deleteResp(r)}
                        data-testid={`btn-del-resp-${r.key}`}
                      >
                        <TrashSimple size={14} />
                      </Button>
                    </div>
                  </div>
                  {r.description && (
                    <p className="text-xs text-slate-500 mb-3 leading-relaxed">{r.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {(r.module_access || []).length === 0 ? (
                      <span className="text-xs text-slate-400 italic">modül atanmamış</span>
                    ) : (
                      (r.module_access || []).map((slug) => {
                        const m = modules.find((x) => x.slug === slug);
                        return (
                          <span key={slug} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200">
                            {m ? m.label : slug}
                          </span>
                        );
                      })
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ---------- Kullanıcı Atamaları sekmesi ---------- */}
          <TabsContent value="users" className="mt-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="text-sm text-slate-600">
                Her kullanıcıya tek bir sorumluluk ve erişebileceği şantiyeleri atayın
              </div>
              <div className="relative w-full md:w-80">
                <MagnifyingGlass size={16} weight="bold"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kullanıcı ara (isim veya e-posta)…"
                  className="pl-9"
                  data-testid="roles-search-input"
                />
              </div>
            </div>

            <Card className="overflow-hidden border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="user-resp-matrix">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[220px]">
                        Kullanıcı
                      </th>
                      <th className="text-center px-2 py-3 font-semibold text-slate-700 min-w-[90px]">
                        Rol
                      </th>
                      <th className="text-left px-2 py-3 font-semibold text-slate-700 min-w-[220px]">
                        Sorumluluk
                      </th>
                      <th className="text-center px-2 py-3 font-semibold text-slate-700 min-w-[160px]">
                        Erişebildiği Şantiyeler
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 min-w-[180px]">
                        Efektif Erişim
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="text-center py-12 text-slate-400">
                        Yükleniyor…
                      </td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-12 text-slate-400">
                        Kullanıcı bulunamadı.
                      </td></tr>
                    ) : filtered.map((u) => {
                      const admin = isAdmin(u);
                      return (
                        <tr
                          key={u.id}
                          className={`border-b border-slate-100 hover:bg-slate-50/50 ${admin ? "bg-sky-50/30" : ""}`}
                          data-testid={`row-user-${u.email}`}
                        >
                          <td className="px-4 py-3 sticky left-0 bg-white">
                            <div className="font-medium text-slate-900">{u.name || "—"}</div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                          </td>
                          <td className="text-center px-2 py-3">
                            {admin ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold" title="Admin whitelist'i yok sayar">
                                <Crown size={12} weight="fill" /> Yönetici
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">
                                Kullanıcı
                              </span>
                            )}
                          </td>
                      {responsibilities.length === 0 ? (
                        <td className="px-2 py-3 text-xs text-slate-400 italic">
                          Önce "Sorumluluklar" sekmesinden tanımlayın
                        </td>
                      ) : (
                        <td className="px-2 py-3">
                          <Select
                            value={currentRespId(u) || "__none__"}
                            onValueChange={(v) => setUserResp(u, v)}
                            disabled={savingUid === u.id}
                          >
                            <SelectTrigger
                              className="h-9 text-sm"
                              data-testid={`resp-select-${u.email}`}
                            >
                              <SelectValue placeholder="Seçiniz..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">
                                <span className="text-slate-500">— Sorumluluk yok —</span>
                              </SelectItem>
                              {responsibilities.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                      <td className="text-center px-2 py-3">
                        <UserSitesPopover
                          user={u}
                          sites={sites}
                          disabled={savingUid === u.id || admin}
                          onChange={(next) => setUserSites(u, next)}
                        />
                      </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${admin ? "text-amber-700" : (u.effective_module_access?.length ? "text-emerald-700" : "text-slate-400")}`}>
                              {effectiveSummary(u)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* ---------- Yöneticiler sekmesi (Faz 52.5) ---------- */}
          <TabsContent value="admins" className="mt-4 space-y-4">
            <Card className="p-4 border-rose-200 bg-rose-50/50">
              <div className="flex items-start gap-3">
                <Shield size={20} weight="duotone" className="text-rose-600 shrink-0 mt-0.5" />
                <div className="text-sm text-rose-900 leading-relaxed">
                  <b>Yönetici Yetkisi:</b> Admin kullanıcılar tüm modüllere ve
                  tüm şantiyelere otomatik erişir, sorumluluk şablonlarını
                  yönetir ve kullanıcı atamalarını değiştirir. Yalnızca gerçekten
                  gerekli kişilere verin. Root yönetici (
                  <span className="font-mono text-xs">{ROOT_ADMIN_EMAIL}</span>)
                  yöneticilikten çıkarılamaz.
                </div>
              </div>
            </Card>
            <Card className="overflow-hidden border-slate-200">
              <table className="w-full text-sm" data-testid="admin-list">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Kullanıcı</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">E-posta</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Atanmış Roller</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-700 w-40">Yönetici</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="text-center py-12 text-slate-400">Yükleniyor…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={4} className="text-center py-12 text-slate-400">Kullanıcı bulunamadı.</td></tr>
                  ) : filtered.map((u) => {
                    const admin = isAdmin(u);
                    const isRoot = (u.email || "").toLowerCase() === ROOT_ADMIN_EMAIL;
                    const assignedResps = (u.responsibilities || [])
                      .map((rid) => responsibilities.find((r) => r.id === rid))
                      .filter(Boolean);
                    return (
                      <tr
                        key={u.id}
                        className={`border-b border-slate-100 hover:bg-slate-50/50 ${admin ? "bg-amber-50/40" : ""}`}
                        data-testid={`admin-row-${u.email}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {u.name || "—"}
                          {isRoot && (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-bold uppercase tracking-wider">
                              <Lock size={10} weight="fill" /> Root
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-1">
                            {admin ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                                <Crown size={12} weight="fill" /> Yönetici
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                                Kullanıcı
                              </span>
                            )}
                            {assignedResps.map((r) => (
                              <span
                                key={r.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 text-[11px] font-medium"
                                title={r.description}
                              >
                                {r.name}
                              </span>
                            ))}
                            {assignedResps.length === 0 && !admin && (
                              <span className="text-xs text-slate-400 italic">pozisyon atanmamış</span>
                            )}
                          </div>
                        </td>
                        <td className="text-center px-4 py-3">
                          <Button
                            variant={admin ? "outline" : "default"}
                            size="sm"
                            disabled={savingUid === u.id || isRoot}
                            onClick={() => toggleAdmin(u, !admin)}
                            className={admin ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "bg-amber-600 hover:bg-amber-700 text-white"}
                            data-testid={`toggle-admin-${u.email}`}
                            title={isRoot ? "Root yönetici kaldırılamaz" : ""}
                          >
                            {admin ? "Kaldır" : "Yönetici Yap"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-slate-400 text-center">
          Değişiklikler otomatik olarak kaydedilir. Erişim güncellemesi, kullanıcı
          sayfayı yenilediğinde veya yeniden giriş yaptığında etkili olur.
        </p>
      </div>

      {/* ---------- Sorumluluk Edit Dialog ---------- */}
      <Dialog open={respOpen} onOpenChange={setRespOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {respEditing ? "Sorumluluğu Düzenle" : "Yeni Sorumluluk"}
            </DialogTitle>
            <DialogDescription>
              Bir pozisyon/görev şablonu tanımlayın ve bu şablona hangi modüllere
              erişim sağlanacağını seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>İsim <span className="text-rose-500">*</span></Label>
              <Input
                value={respForm.name}
                onChange={(e) => setRespForm({ ...respForm, name: e.target.value })}
                placeholder="Örn. Şantiye Şefi"
                data-testid="resp-name-input"
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                rows={2}
                value={respForm.description}
                onChange={(e) => setRespForm({ ...respForm, description: e.target.value })}
                placeholder="Bu sorumluluğun kısa açıklaması…"
                data-testid="resp-desc-input"
              />
            </div>
            <div>
              <Label>Modül Erişimleri</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-slate-200 rounded-md bg-slate-50/50">
                {modules.map((m) => {
                  const checked = respForm.module_access.includes(m.slug);
                  return (
                    <label
                      key={m.slug}
                      className="flex items-start gap-2 cursor-pointer hover:bg-white rounded px-2 py-1.5"
                      data-testid={`resp-mod-${m.slug}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRespModule(m.slug)}
                      />
                      <div className="text-xs leading-tight">
                        <div className="font-medium text-slate-900">{m.label}</div>
                        <div className="text-slate-500">{m.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespOpen(false)} disabled={respSaving}>
              Vazgeç
            </Button>
            <Button
              onClick={saveResp}
              disabled={respSaving}
              className="bg-[#1B4776] hover:bg-[#163a63] text-white"
              data-testid="resp-save-btn"
            >
              {respSaving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
